import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Segmented } from 'antd';
import { SunOutlined, MoonOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faCompressArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { AREA_TYPES, type AreaType } from '../../../constants/areaType';
import { LINK_TYPES, type LinkType } from '../../../constants/linkType';
import { useFeatureOptionsData } from '../../../hooks/data/useFeatureOptionsData';
import { useMapVisibility } from '../../../hooks/SimViewer/useMapVisibility';
import { useMapColor } from '../../../hooks/SimViewer/useMapColor';
import { useThreeDMode } from '../../../hooks/SimViewer/useThreeDMode';
import { useMapColorPalette } from '../../../hooks/SimViewer/useMapColorPalette';
import { toCamel } from '../../../utils/case';
import { EyeIconButton } from '../../EyeIconButton';

type Props = {
  menuOpen: boolean;
  toggleMenuOpen: () => void;
};

const MapAppearanceSection = ({
  menuOpen,
  toggleMenuOpen,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { featureOptions } = useFeatureOptionsData();
  const {
    visibleAreaType,
    visibleLinkTypes,
    setVisibleAreaType,
    toggleVisibleLinkType,
  } = useMapVisibility();
  const { mapColor, setMapColor } = useMapColor();
  const { isThreeDMode } = useThreeDMode();
  const { setMapColorOpacity } = useMapColorPalette();

  if (!menuOpen) {
    return (
      <Button
        size="large"
        icon={<FontAwesomeIcon icon={faMap} size="lg" color="#707070" />}
        onClick={toggleMenuOpen}
      />
    );
  }
  return (
    <Section>
      <SectionHeader>
        <FontAwesomeIcon icon={faMap} size="lg" color="#707070" />
        <HeaderLabel>{t('map')}</HeaderLabel>
        <HeaderAction>
          <Button
            icon={
              <FontAwesomeIcon
                icon={faCompressArrowsAlt}
                size="sm"
                color="#9D9D9D"
              />
            }
            type="text"
            shape="circle"
            size="small"
            onClick={toggleMenuOpen}
          />
        </HeaderAction>
      </SectionHeader>
      <SectionItem>
        <SectionItemLabel>{t('theme')}</SectionItemLabel>
        <SectionItemContent>
          <StyledSegmented
            size="middle"
            value={mapColor}
            options={[
              {
                value: 'light',
                label: t('light'),
                icon: <SunOutlined style={{ fontSize: '14px' }} />,
              },
              {
                value: 'dark',
                label: t('dark'),
                icon: <MoonOutlined style={{ fontSize: '14px' }} />,
              },
            ]}
            onChange={(value: string | number): void => {
              if (value === 'light' || value === 'dark') {
                setMapColor(value);
                setMapColorOpacity(
                  value === 'light'
                    ? isThreeDMode
                      ? 0.6
                      : 0.5
                    : isThreeDMode
                      ? 0.8
                      : 0.7
                );
              }
            }}
          />
        </SectionItemContent>
      </SectionItem>
      <SectionItem>
        <SectionItemLabel>{t('areas.state')}</SectionItemLabel>
        <SectionItemContent>
          <GridContainer>
            {AREA_TYPES.map((areaType: AreaType): ReactNode => {
              return (
                <IconLabelContainer key={areaType}>
                  <EyeIconButton
                    active={visibleAreaType === areaType}
                    onClick={(): void => {
                      setVisibleAreaType(
                        visibleAreaType === areaType ? null : areaType
                      );
                    }}
                  />
                  {t(`areas.${areaType}`, { defaultValue: areaType })}
                </IconLabelContainer>
              );
            })}
          </GridContainer>
        </SectionItemContent>
      </SectionItem>
      {featureOptions?.display_link && (
        <SectionItem>
          <SectionItemLabel>{t('roadStreet')}</SectionItemLabel>
          <SectionItemContent>
            <GridContainer>
              {LINK_TYPES.map((linkType: LinkType): ReactNode => {
                return (
                  <IconLabelContainer key={linkType}>
                    <EyeIconButton
                      active={visibleLinkTypes.includes(linkType)}
                      onClick={(): void => {
                        toggleVisibleLinkType(linkType);
                      }}
                    />
                    {t(`link.${toCamel(linkType)}`, { defaultValue: linkType })}
                  </IconLabelContainer>
                );
              })}
            </GridContainer>
          </SectionItemContent>
        </SectionItem>
      )}
    </Section>
  );
};

const Section = styled.div`
  width: 100%;
  background: #fafafa;
  padding: 0 0 8px 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #d9d9d9;
`;

const HeaderLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  line-height: 16px;
`;

const HeaderAction = styled.div`
  margin-left: auto;
`;

const SectionItem = styled.div`
  margin: 8px 8px 0 8px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const SectionItemLabel = styled.div`
  line-height: 1.3;
  font-size: 12px;
  font-weight: 600;
`;

const SectionItemContent = styled.div`
  flex: 1 1 auto;
  min-width: 50%;
  text-align: right;
`;

const StyledSegmented = styled(Segmented)`
  .ant-segmented-item-label {
    height: 24px;
    min-height: 24px;
    line-height: 24px;
    padding: 0 8px;
  }
`;

const GridContainer = styled.div`
  min-width: 200px;
  margin: 0 8px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
  gap: 0px 16px;
`;

const IconLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
  gap: 0;
`;

export { MapAppearanceSection };
